import { useContext, useEffect, useRef, useState } from "react";
import { Card } from "../Card/Card";
import { AppContext } from "../State/Context";
import { ActionType } from "../State/State";
import { getCenterHexFromWorld } from "../State/stateUtils";
import { requestHexagenInfo } from "../api/hexagen";
import { Sparks } from "../components/Sparks";

type CardPopupProps = {};

export const CardPopup: React.FC<CardPopupProps> = () => {
  const { dispatch, state } = useContext(AppContext);
  const [isSparks, setIsSparks] = useState(false);
  const hexagenInfo = state.selectedHexInfo;
  const hexagenExtra = state.selectedHexExtra;
  const [isClosing, setIsClosing] = useState(false);
  const selectedHexCoord = state.selectedHexCoord;

  useEffect(() => {
    if (!state.isHexInfoPopupOpen || !selectedHexCoord) return;
    window.history.pushState(null, "", `/hex/${selectedHexCoord.x}/${selectedHexCoord.y}/view`);
    requestHexagenInfo(selectedHexCoord.x, selectedHexCoord.y);
  }, [state.isHexInfoPopupOpen, selectedHexCoord]);

  const lastViewContentHexRef = useRef<string | null>(null);
  useEffect(() => {
    if (!hexagenInfo || !selectedHexCoord) return;
    const key = `${hexagenInfo.x}-${hexagenInfo.y}`;
    if (lastViewContentHexRef.current === key) return;
    lastViewContentHexRef.current = key;
    try {
      if (typeof window !== "undefined" && (window as any).rdt) {
        (window as any).rdt("track", "ViewContent");
      }
      if (typeof window !== "undefined" && (window as any).gtag) {
        (window as any).gtag("event", "select_content", {
          content_type: "hex",
          item_id: key,
        });
      }
    } catch (_) {}
  }, [hexagenInfo, selectedHexCoord]);

  const closePopup = (setUrlToCenter: boolean) => {
    setIsClosing(true);
    const pathToSet =
      setUrlToCenter && typeof window !== "undefined"
        ? (() => {
            const center = getCenterHexFromWorld(
              state.worldX,
              state.worldY,
              state.worldScale,
              state.hexGridBuffer ?? 0
            );
            return center ? `/hex/${center.x}/${center.y}` : null;
          })()
        : null;
    setTimeout(() => {
      if (pathToSet) window.history.replaceState(null, "", pathToSet);
      dispatch({ type: ActionType.CLOSE_HEX_INFO_POPUP });
      dispatch({ type: ActionType.REMOVE_SELECTED_HEX_INFO });
      dispatch({ type: ActionType.REMOVE_SELECTED_HEX_EXTRA });
    }, 600);
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Escape" || !selectedHexCoord) return;
      e.preventDefault();
      e.stopPropagation();
      closePopup(true);
    };
    window.addEventListener("keydown", handleKeyDown, true);
    return () => window.removeEventListener("keydown", handleKeyDown, true);
  }, [selectedHexCoord, dispatch]);

  const onCloseHandler = () => {
    closePopup(true);
  };

  const dataReady =
    hexagenInfo &&
    hexagenExtra &&
    selectedHexCoord &&
    hexagenInfo.x === selectedHexCoord.x &&
    hexagenInfo.y === selectedHexCoord.y;

  const bgClickHandler = (event: React.MouseEvent<HTMLDivElement>) => {
    if (event.target === event.currentTarget) {
      onCloseHandler();
    }
  };

  const setSparks = (value: boolean) => setIsSparks(value);

  if (!selectedHexCoord) {
    return null;
  }

  return (
    <div
      onClick={bgClickHandler}
      className={`fixed overflow-hidden ${
        isClosing ? "disappear-to-opacity-0" : "appear-from-opacity-40"
      } shadow inset-0 flex justify-center items-center z-[10000000000] ${
        isClosing ? "animate-fade-out" : ""
      }`}
      style={{ perspective: "1000px", transformStyle: "preserve-3d", contain: "layout paint" }}
    >
      {isSparks && <Sparks />}
      <Card
        selectedHexCoord={selectedHexCoord}
        hexagenInfo={dataReady ? hexagenInfo : null}
        hexagenExtra={dataReady ? hexagenExtra : null}
        onCloseHandler={onCloseHandler}
        setSparks={setSparks}
        className={`${
          isClosing ? "disappear-to-bottom" : "appear-from-bottom"
        } opacity-0 -rotate-x-4`}
        style={{perspective: "1000px", transformStyle: "preserve-3d" }}
      />
    </div>
  );
};
